import React, { useEffect, useRef, useState } from 'react'
import type { Script } from '@/features/script/domain/models/script.ts'
import {
  formatDateToTimestamp,
  formatSecondsToMinutesSeconds,
} from '@/utils/formatters.ts'
import { calculateScriptDuration } from '@/utils/calculate-script-duration.ts'
import Icon from '@/components/common/Icon'
import ContextMenu from '@/components/common/ContextMenu'
import { deleteScript } from '@/features/script/domain/use-cases/delete-script'

interface ScriptBoxProps {
  script: Script
  onSelect: () => void
}
export const ScriptBox: React.FC<ScriptBoxProps> = ({ script, onSelect }) => {
  const textRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isExpandable, setIsExpandable] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [height, setHeight] = useState<string | undefined>(undefined)
  const [isClamped, setIsClamped] = useState(true)
  const [originalHeight, setOriginalHeight] = useState<number>(0)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const toggleExpand = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (!containerRef.current || !textRef.current) return

    if (isExpanded) {
      // Collapse: use the saved original height
      setHeight(`${originalHeight}px`)
      setIsExpanded(false)
      // The clamp will be applied in onTransitionEnd
    } else {
      // Expand: save current height and expand
      const currentHeight = textRef.current.offsetHeight
      setOriginalHeight(currentHeight)
      setHeight(`${currentHeight}px`)
      setIsClamped(false)
      setIsExpanded(true)
      requestAnimationFrame(() => {
        if (!textRef.current) return
        setHeight(`${textRef.current.scrollHeight}px`)
      })
    }
  }

  const handleTransitionEnd = () => {
    if (!isExpanded) {
      // Applying the clamp when the collapse animation ends
      setIsClamped(true)
    }
  }

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsMenuOpen(false)
    deleteScript(script.id)
  }

  useEffect(() => {
    if (textRef.current) {
      setIsExpandable(
        textRef.current.scrollHeight > textRef.current.clientHeight
      )
    }
  }, [script.text])

  // Reset height and clamp when script changes
  useEffect(() => {
    setHeight(undefined)
    setIsExpanded(false)
    setIsClamped(true)
    setOriginalHeight(0)
  }, [script.text])

  return (
    <div
      className="group relative flex cursor-pointer select-text flex-col gap-1 rounded p-3 transition-colors duration-150 hover:bg-zinc-700"
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-300">
            {formatDateToTimestamp(script.lastUpdate)}
          </span>
          <span className="rounded-full border border-zinc-600 bg-transparent px-3 py-1 text-xs font-medium text-zinc-300">
            {formatSecondsToMinutesSeconds(
              calculateScriptDuration(script.text)
            )}
          </span>
          <div className="relative flex items-center">
            <button
              type="button"
              ref={menuButtonRef}
              className="flex items-center justify-center opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                setIsMenuOpen((open) => !open)
              }}
            >
              <Icon name="menuHorizontal" width={24} height={24} />
            </button>
            <ContextMenu
              viewModel={[
                {
                  key: 'ls_generic_delete',
                  action: handleDelete,
                  icon: <Icon name="trash" width={20} height={20} />,
                },
              ]}
              isOpen={isMenuOpen}
              setIsOpen={setIsMenuOpen}
              position={{ left: 0, top: 28 }}
              triggerRef={menuButtonRef}
            />
          </div>
        </div>
        {isExpandable && (
          <div
            className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
            onClick={toggleExpand}
          >
            <Icon
              name={isExpanded ? 'chevronUp' : 'chevronDown'}
              width={24}
              height={24}
            />
          </div>
        )}
      </div>
      <div
        ref={containerRef}
        style={{
          height: height,
          overflow: 'hidden',
          transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div
          ref={textRef}
          className={`${isClamped ? 'line-clamp-3' : ''} whitespace-pre-line text-sm font-medium leading-tight text-white transition-all duration-500 ease-in-out`}
        >
          {script.text}
        </div>
      </div>
    </div>
  )
}
